/* eslint-disable react-hooks/exhaustive-deps */
import { Avatar, Badge, Box, Skeleton, styled } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import { socialPlatformsIcons } from '../../../investigate/Content/routes/collections/Collection/constants';
import { ProfilePicture } from '../../models/collection-types';
import userFallback from '../../images/fallbacks/avatar-fallback.svg';
import { LoadAssetWrapper } from '@cellebrite/saas-ui-common';
import { SRC_PREFIX } from '../../constants/constants';

const SmallAvatar = styled(Avatar)(({ theme }) => ({
    width: 22,
    height: 22,
}));

interface CustomAvatarProps {
    avatar?: ProfilePicture;
    styles?: object;
    avatarStyles?: object;
    avatarSize?: string;
    smallAvatarSize?: string;
    onClick?: any;
    title?: string;
    skipLoadAssetWrapper?: boolean;
}

const unknown = 'unknown';
const CustomAvatar: React.FC<CustomAvatarProps> = ({
    avatar,
    styles,
    avatarSize,
    smallAvatarSize,
    onClick,
    avatarStyles,
    title,
    skipLoadAssetWrapper,
}) => {
    const avatarRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [onChildLoad, setOnChildLoad]: any = useState(null);
    const finalAvatarStyles = {
        width: avatarSize || 48,
        height: avatarSize || 48,
        zIndex: 0,
        ...avatarStyles,
    };
    const platform = useMemo(() => {
        return socialPlatformsIcons.get(avatar.source?.toUpperCase());
    }, [avatar]);
    const platformImage = useMemo(() => {
        let result = { src: '', type: '' };
        try {
            if (avatar.badgeDirectSrc) {
                result.src = avatar.source;
                result.type = unknown;
            } else {
                result.src = require(`../../../common/images/social-icons/${platform}.svg`);
                result.type = platform;
            }
        } catch (e) {
            if (avatar.source) {
                result.src = require(`../../../common/images/social-icons/${unknown}.svg`);
                result.type = unknown;
            }
        }
        result.src = result.src.toString();
        return result;
    }, [avatar]);

    const src = useMemo(() => {
        const finalSrc = avatar.imgLocalSrc || avatar.imgSrc;
        return finalSrc ? `${SRC_PREFIX}${finalSrc}` : finalSrc;
    }, [avatar]);

    const content = (
        <Box title={title || ''} data-testid="custom-image" onClick={(event) => onClick?.(event)}>
            {avatar && (
                <Badge
                    sx={{ padding: 0, zIndex: 0, ...styles }}
                    badgeContent={
                        platformImage.src && (
                            <SmallAvatar
                                className={`avatar-border ${
                                    platformImage.type === unknown ? 'background-color-l' : 'unset'
                                }`}
                                sx={{
                                    'height': smallAvatarSize,
                                    'width': smallAvatarSize,
                                    '& img': {
                                        width: platformImage.type === unknown ? '72.75%' : null,
                                        height: platformImage.type === unknown ? '72.75%' : null,
                                    },
                                }}
                                src={platformImage.src}
                            />
                        )
                    }
                    overlap="circular"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                >
                    {loading && <Skeleton sx={finalAvatarStyles} variant="circular" />}
                    <Avatar
                        ref={avatarRef}
                        className="avatar-border"
                        sx={{ ...finalAvatarStyles, ...{ display: loading ? 'none' : 'flex' } }}
                        src={src}
                        onLoad={() => {
                            setLoading(false);
                            onChildLoad?.();
                        }}
                        onError={() => {
                            setLoading(false);
                            onChildLoad?.();
                        }}
                    />
                </Badge>
            )}
        </Box>
    );

    return skipLoadAssetWrapper ? (
        content
    ) : (
        <LoadAssetWrapper
            setOnChildLoad={setOnChildLoad}
            key={src}
            assetUrl={src}
            skeleton={
                <Badge
                    sx={{ padding: 0, zIndex: 0, ...styles }}
                    overlap="circular"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                >
                    <Skeleton sx={finalAvatarStyles} variant="circular" />
                </Badge>
            }
            fallback={
                <Badge
                    sx={{ padding: 0, zIndex: 0, ...styles }}
                    badgeContent={
                        platformImage.src && (
                            <SmallAvatar
                                className={`avatar-border ${
                                    platformImage.type === unknown ? 'background-color-l' : 'unset'
                                }`}
                                sx={{
                                    'height': smallAvatarSize,
                                    'width': smallAvatarSize,
                                    '& img': {
                                        width: platformImage.type === unknown ? '66.6%' : null,
                                        height: platformImage.type === unknown ? '66.6%' : null,
                                    },
                                }}
                                src={platformImage.src}
                            />
                        )
                    }
                    overlap="circular"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                >
                    <Avatar sx={finalAvatarStyles} src={userFallback} />
                </Badge>
            }
        >
            {content}
        </LoadAssetWrapper>
    );
};

export default CustomAvatar;
